// themes.ts — the interface palettes, shared by the terminal and the site
// (owner 27.09.2026: the two products must look the same). `graphite` is
// the default. Labels are plain English: render them through tr().
//
// `dark` and `light` are the tokens.css baselines; the rest are applied as
// an inline CSS-var bundle on <html> (same mechanism as designTokens), over
// whichever base (`data-theme`) they sit closest to. Every theme sets the
// candle colours (--bull / --bear), so the chart follows.

export type ThemeId = 'graphite' | 'dark' | 'light' | 'midnight' | 'slate' | 'nocturne' | 'viridian' | 'dawn' | 'colorblind' | 'contrast';
export const DEFAULT_THEME: ThemeId = 'graphite';

export interface ThemeDef {
  id: ThemeId;
  label: string;
  /** Which tokens.css block to fall back to for anything not overridden. */
  base: 'dark' | 'light';
  /** One-liner shown under the swatch. */
  hint: string;
  /** Compact colour spec — washes/shadows are derived. */
  c: {
    void: string; s1: string; s2: string; s3: string; s4: string;
    line: string; line2: string; line3: string;
    hi: string; mid: string; low: string; dis: string;
    acc: string; accHi: string;
    bull: string; bear: string; warn: string; violet: string;
  };
}

const rgba = (hex: string, a: number) => {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((x) => x + x).join('') : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

/** a·t + b·(1−t), per channel, as #RRGGBB — canvas-safe (no color-mix). */
const mixHex = (a: string, b: string, t: number) => {
  const p = (h: string) => { const n = parseInt(h.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
  const A = p(a), Bc = p(b);
  return '#' + A.map((x, i) => Math.round(x * t + Bc[i] * (1 - t)).toString(16).padStart(2, '0')).join('').toUpperCase();
};

export const THEMES: ThemeDef[] = [
  {
    id: 'graphite', label: 'Graphite', base: 'dark', hint: 'Neutral graphite, calm green — the default',
    c: {
      void: '#0C0F13', s1: '#12161B', s2: '#171C22', s3: '#1E242B', s4: '#262D35',
      line: '#222830', line2: '#2B323B', line3: '#3A424D',
      hi: '#E9ECEF', mid: '#9BA4AE', low: '#656E79', dis: '#3D444D',
      acc: '#3DDC97', accHi: '#5FE6AC', bull: '#3DDC97', bear: '#F2607F', warn: '#F5B454', violet: '#9A8CFF',
    },
  },
  {
    id: 'dark', label: 'Classic dark', base: 'dark', hint: 'The default — brand green on near-black',
    c: {
      void: '#05070E', s1: '#0A0E18', s2: '#111726', s3: '#18202F', s4: '#1E2740',
      line: '#1B2233', line2: '#2A3346', line3: '#3A465E',
      hi: '#E6EAF2', mid: '#8C96AC', low: '#566079', dis: '#3A4256',
      acc: '#2CF5A7', accHi: '#6BFFCB', bull: '#2CF5A7', bear: '#FF5C87', warn: '#FFB84D', violet: '#8B7CFF',
    },
  },
  {
    id: 'light', label: 'Light', base: 'light', hint: 'Warm paper — soft on the eyes in daylight',
    c: {
      void: '#D8D2C5', s1: '#EAE5DA', s2: '#E2DCCF', s3: '#D8D1C2', s4: '#CCC3B0',
      line: '#CBC2AF', line2: '#BAAF98', line3: '#9E9179',
      hi: '#1C1914', mid: '#4A4337', low: '#6F6754', dis: '#A0977F',
      acc: '#22774A', accHi: '#2B8C58', bull: '#22774A', bear: '#AE4636', warn: '#8A600F', violet: '#5F50AC',
    },
  },
  {
    id: 'midnight', label: 'Midnight', base: 'dark', hint: 'Deep navy, sky-blue accent — the calmest dark',
    c: {
      void: '#060912', s1: '#0A1020', s2: '#10182C', s3: '#17223C', s4: '#1E2C4C',
      line: '#1A2540', line2: '#2A3A5E', line3: '#3C5080',
      hi: '#E4ECF7', mid: '#8A9AB8', low: '#566588', dis: '#38455F',
      acc: '#38BDF8', accHi: '#7DD3FC', bull: '#34D399', bear: '#FB7185', warn: '#FBBF24', violet: '#A78BFA',
    },
  },
  {
    id: 'slate', label: 'Slate', base: 'dark', hint: 'Warm graphite, amber accent — low saturation',
    c: {
      void: '#0D0D0F', s1: '#141416', s2: '#1B1B1E', s3: '#242428', s4: '#2E2E34',
      line: '#262629', line2: '#37373D', line3: '#4C4C54',
      hi: '#ECECEA', mid: '#9A9A94', low: '#66665F', dis: '#454540',
      acc: '#E0A458', accHi: '#EDBE82', bull: '#7FB069', bear: '#D6685A', warn: '#E0A458', violet: '#9B8BC4',
    },
  },
  {
    id: 'nocturne', label: 'Nocturne', base: 'dark', hint: 'Plum-tinted, violet accent — the design palette',
    c: {
      void: '#131320', s1: '#1A1A2B', s2: '#232338', s3: '#2D2D47', s4: '#383858',
      line: '#2A2A42', line2: '#3D3D5C', line3: '#55557A',
      hi: '#E9E9EF', mid: '#9A97B5', low: '#6A6788', dis: '#47476A',
      acc: '#9184D9', accHi: '#A99EDF', bull: '#4BD0A0', bear: '#F2708A', warn: '#F2C14E', violet: '#B8A9FF',
    },
  },
  {
    id: 'viridian', label: 'Viridian', base: 'dark', hint: 'Near-black, phosphor green — the terminal look',
    c: {
      void: '#050806', s1: '#081109', s2: '#0D1A0F', s3: '#142718', s4: '#1C3622',
      line: '#142A18', line2: '#244A2C', line3: '#356B40',
      hi: '#D8F0DD', mid: '#7BA585', low: '#4D7A56', dis: '#2E4C36',
      acc: '#34D058', accHi: '#5BE07C', bull: '#34D058', bear: '#F2555A', warn: '#E8C547', violet: '#6AD0A0',
    },
  },
  {
    id: 'dawn', label: 'Dawn', base: 'light', hint: 'Cool blue-grey light — crisp and bright',
    c: {
      void: '#E6EAF0', s1: '#FFFFFF', s2: '#F4F6FA', s3: '#EAEEF5', s4: '#DDE4EE',
      line: '#E2E7EF', line2: '#CFD7E3', line3: '#AEB9C9',
      hi: '#1E2733', mid: '#56627A', low: '#8A94A6', dis: '#B4BFCF',
      acc: '#4A7FD0', accHi: '#6E9EE0', bull: '#1E9E6B', bear: '#D64A6B', warn: '#C08430', violet: '#6A5FC0',
    },
  },
  {
    // owner 08.10.2026: pure black, white text, saturated candles and strong lines — for glare and for tired eyes.
    id: 'contrast', label: 'High contrast', base: 'dark', hint: 'Pure black, white text, strong lines',
    c: {
      void: '#000000', s1: '#0B0B0B', s2: '#141414', s3: '#1E1E1E', s4: '#2A2A2A',
      line: '#3A3A3A', line2: '#5A5A5A', line3: '#8A8A8A',
      hi: '#FFFFFF', mid: '#D0D0D0', low: '#9A9A9A', dis: '#666666',
      acc: '#00FF99', accHi: '#66FFC2', bull: '#00FF7F', bear: '#FF3B5C', warn: '#FFD400', violet: '#B49CFF',
    },
  },
  {
    // UX-014 (roadmap "Желательно"): red/green is exactly the pair most
    // colour-vision deficiencies confuse — bid/ask, long/short, PnL all
    // lean on it. Swapped for the Okabe-Ito colour-blind-safe pair (blue /
    // vermillion) instead of a hue tweak, same dark surfaces as `dark` so
    // this reads as "the usual terminal, recoloured" not a new skin.
    id: 'colorblind', label: 'Colour-blind safe', base: 'dark', hint: 'Blue/orange instead of green/red (Okabe-Ito palette)',
    c: {
      void: '#05070E', s1: '#0A0E18', s2: '#111726', s3: '#18202F', s4: '#1E2740',
      line: '#1B2233', line2: '#2A3346', line3: '#3A465E',
      hi: '#E6EAF2', mid: '#8C96AC', low: '#566079', dis: '#3A4256',
      acc: '#4DA8F5', accHi: '#7EC4FF', bull: '#4DA8F5', bear: '#E67300', warn: '#F0E442', violet: '#CC79A7',
    },
  },
];

export function themeDef(id: string): ThemeDef {
  return THEMES.find((t) => t.id === id) ?? THEMES[0];
}

/** The full inline CSS-var map for a theme — the compact spec plus derived
 * deep tints, washes and shadows. */
export function themeVars(id: string): Record<string, string> {
  const t = themeDef(id);
  const c = t.c;
  const dark = t.base === 'dark';
  return {
    '--void': c.void, '--s1': c.s1, '--s2': c.s2, '--s3': c.s3, '--s4': c.s4,
    '--line': c.line, '--line2': c.line2, '--line3': c.line3,
    '--hi': c.hi, '--mid': c.mid, '--low': c.low, '--dis': c.dis,
    '--acc': c.acc, '--acc-hi': c.accHi, '--acc-deep': rgba(c.acc, dark ? 0.16 : 0.16),
    '--bull': c.bull, '--bear': c.bear, '--warn': c.warn, '--violet': c.violet,
    '--t-num-bull': mixHex(c.bull, c.hi, 0.85), '--t-num-bear': mixHex(c.bear, c.hi, 0.85),
    '--bull-deep': rgba(c.bull, 0.16), '--bear-deep': rgba(c.bear, 0.16),
    '--warn-deep': rgba(c.warn, 0.16), '--violet-deep': rgba(c.violet, 0.16),
    '--neutral-deep': c.line,
    '--surface-float': rgba(c.s2, dark ? 0.86 : 0.97),
    '--level': c.low, '--liq': c.bear,
    '--on-acc': dark ? '#04121F' : '#FFFFFF', '--on-bull': dark ? '#04140D' : '#FFFFFF', '--on-bear': dark ? '#1A0308' : '#FFFFFF',
    '--w-bull': rgba(c.bull, 0.14), '--w-bear': rgba(c.bear, 0.14),
    '--w-bull-strong': rgba(c.bull, 0.28), '--w-bear-strong': rgba(c.bear, 0.28),
    '--w-warn': rgba(c.warn, 0.16), '--w-acc': rgba(c.acc, 0.14),
    '--w-acc-border': rgba(c.acc, 0.45), '--w-violet': rgba(c.violet, 0.14),
    '--w-liq': rgba(c.bear, 0.18),
    '--w-hover': dark ? 'rgba(233,236,239,.04)' : 'rgba(20,40,90,.05)',
    '--w-zebra': dark ? 'rgba(255,255,255,.025)' : 'rgba(0,0,0,.025)',
    '--w-scrim': dark ? 'rgba(3,5,10,.62)' : 'rgba(20,28,40,.4)',
    '--shadow-float': dark
      ? '0 18px 50px rgba(0,0,0,.55), 0 0 0 1px rgba(150,175,225,.08)'
      : '0 18px 50px rgba(30,45,80,.22), 0 0 0 1px rgba(20,40,90,.08)',
    '--shadow-dropdown': dark
      ? '0 10px 28px rgba(0,0,0,.5), 0 0 0 1px rgba(150,175,225,.07)'
      : '0 10px 28px rgba(30,45,80,.18), 0 0 0 1px rgba(20,40,90,.07)',
  };
}
